import { useEffect, useState } from 'react';

/**
 * Bootstrap configurator state from navigation (edit saved drawing / keep board after update).
 * @param {object} location - react-router location
 * @param {object|function} defaultConfig - factory result for fresh config
 * @param {(loaded: object) => object} [transform] - optional merge/normalize
 */
export function useConfiguratorLoadState(location, defaultConfig, transform) {
  const loadConfig = location.state?.loadQuoteConfig;
  const editQuoteId = location.state?.editQuoteId || null;

  const resolveLoadedConfig = (source) => {
    const cloned = JSON.parse(JSON.stringify(source));
    return transform ? transform(cloned) : cloned;
  };

  const resolveDefault = () =>
    typeof defaultConfig === 'function' ? defaultConfig() : defaultConfig;

  const [config, setConfig] = useState(() => {
    if (loadConfig && typeof loadConfig === 'object' && !Array.isArray(loadConfig)) {
      return resolveLoadedConfig(loadConfig);
    }
    return resolveDefault();
  });

  // Bumps when a drawing is loaded from navigation so dirty baseline can be reset
  const [navLoadToken, setNavLoadToken] = useState(0);
  const [lastNavLoadedConfig, setLastNavLoadedConfig] = useState(null);

  // Re-apply when navigation provides a drawing to show (Edit drawing, or after Update drawing)
  useEffect(() => {
    const nextLoad = location.state?.loadQuoteConfig;
    if (!nextLoad || typeof nextLoad !== 'object' || Array.isArray(nextLoad)) {
      return;
    }
    const resolved = resolveLoadedConfig(nextLoad);
    setConfig(resolved);
    setLastNavLoadedConfig(resolved);
    setNavLoadToken((n) => n + 1);
    // Only react to navigation — not to transform identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return {
    config,
    setConfig,
    editQuoteId,
    isEditingDrawing: Boolean(editQuoteId),
    navLoadToken,
    lastNavLoadedConfig,
  };
}
